import { isEqual } from "lodash-es";
import z4 from "zod/v4";
import type { $ZodErrorTree } from "zod/v4/core";
import { config } from "../../../config";
import { isEmailChannel } from "../../channels/types";
import { usesRestrictedSegmentOrEmail } from "../../moment/component/buttons/uses-restricted-data";
import { getToTarget } from "../../to/to-target";
import type { FollowUpMomentDetailsConfig } from "../hooks/use-follow-up-moment-details";
import { followUpToHandleSaveMoment } from "../server/follow-up-to-handle-save-moment";
import { momentToFollowUp } from "../server/moment-to-follow-up";
import type { FollowUpDetailsWithConfig } from "../server/types";
import { isEmailChannelWithData } from "../utils";
import type { FollowUpPageAction, FollowUpPageState } from "./actions";
import type { FollowUpChannelWithData, FollowUpSchemaType } from "./schema";
import { followUpSchema, followUpSchemaNewsletter } from "./schema";
import { switchChannel } from "./switch-channel";

export function followUpReducer(
  state: FollowUpPageState,
  action: FollowUpPageAction,
): FollowUpPageState {
  const newUI = state.ui;

  switch (action.type) {
    case "SET_SAVING":
      return {
        ...state,
        saveState: { saving: true, error: null },
      };
    case "FINISHED_SAVING":
      return {
        ...state,
        saveState: { saving: false, error: action.payload },
      };

    case "LOAD_START":
      return {
        ...state,
        momentDetails: { data: null, loading: true, error: null },
      };

    case "LOAD_SUCCESS": {
      const loadedUi: FollowUpPageState["ui"] = {
        ...state.ui,
        ...momentToFollowUp(
          action.payload.momentFollowUpDetails,
          action.payload.followUp,
          state.followUpMomentDetailsConfig,
        ),
      };

      // For non-lexical (newsletter) moments, the email subject is stored in
      // email_moment.subject rather than moment.email_subject. Backfill it
      // into channelData so the newsletter editor initialises with the correct value.
      if (
        !action.payload.useLexicalEditor &&
        action.payload.sourceEmailMomentSubject &&
        isEmailChannelWithData(loadedUi)
      ) {
        const { email_subject } = loadedUi.channelData;
        if (!email_subject) {
          loadedUi.channelData = {
            ...loadedUi.channelData,
            email_subject: action.payload.sourceEmailMomentSubject,
          };
        }
      }

      // For non-lexical (newsletter) moments, the sender is stored in
      // email_moment.from_address rather than moment.from. Backfill it
      // into channelData so the newsletter editor initialises with the correct sender.
      if (
        !action.payload.useLexicalEditor &&
        action.payload.sourceEmailMoment?.from_address &&
        action.payload.sourceEmailMoment.from_address !== config.defaultEmailFrom &&
        isEmailChannelWithData(loadedUi)
      ) {
        const { email_sender } = loadedUi.channelData;
        if (!email_sender || email_sender === config.defaultEmailFrom) {
          loadedUi.channelData = {
            ...loadedUi.channelData,
            email_sender: action.payload.sourceEmailMoment.from_address,
          };
        }
      }

      const emailMoment = action.payload.followUp?.emailMoment ?? null;

      return deriveDerivedState(
        {
          ...state,
          momentDetails: { data: action.payload, loading: false, error: null },
          initialUi: loadedUi,
          useLexicalEditor: action.payload.useLexicalEditor,
          initialUseLexicalEditor: action.payload.useLexicalEditor,
          emailMoment,
          initialEmailMoment: emailMoment,
          dynamicSegmentIdMap: action.payload.dynamicSegmentIdMap ?? null,
        },
        loadedUi,
      );
    }

    case "LOAD_ERROR":
      return {
        ...state,
        momentDetails: { data: null, loading: false, error: action.error },
      };

    case "SET_TITLE":
      return deriveDerivedState(state, { ...newUI, title: action.payload });

    case "SET_REASON":
      return deriveDerivedState(state, { ...newUI, reason: action.payload });

    case "SET_CHANNEL": {
      const channelState = isEmailChannel(action.payload.channel)
        ? {
            ...state,
            useLexicalEditor: state.initialUseLexicalEditor,
          }
        : { ...state, emailMoment: null, useLexicalEditor: true };

      return deriveDerivedState(channelState, {
        ...newUI,
        ...(action.resetDesign
          ? { ...action.payload, design: null }
          : switchChannel(
              state.ui.channel,
              action.payload,
              state.ui.design,
              state.momentDetails.data?.slackVideoEnabled ?? false,
            )),
      });
    }

    case "SET_SCHEDULE":
      return deriveDerivedState(state, { ...newUI, ...action.payload });

    case "SET_MESSAGE_DESIGN":
      return deriveDerivedState(state, { ...newUI, design: action.payload });

    case "SET_EMAIL_MOMENT":
      return deriveDerivedState({ ...state, emailMoment: action.payload }, state.ui);

    case "SET_ERRORS_DISPLAY":
      return { ...state, errors: { ...state.errors, display: action.payload } };

    case "FINISHED_PAUSE": {
      const details = state.momentDetails.data;
      const followUp = details?.followUp;

      if (!details || !followUp) return state;

      return deriveDerivedState(
        {
          ...state,
          saveState: { saving: false, error: null },
          momentDetails: {
            ...state.momentDetails,
            data: {
              ...details,
              followUp: { ...followUp, running: false, status: "paused" },
            },
          },
        },
        state.ui,
      );
    }

    case "FINISHED_PUBLISH": {
      const details = state.momentDetails.data;
      const followUp = details?.followUp;

      if (!details || !followUp) return state;

      return deriveDerivedState(
        {
          ...state,
          saveState: { saving: false, error: null },
          momentDetails: {
            ...state.momentDetails,
            data: {
              ...details,
              followUp: { ...followUp, running: true, status: "active" },
            },
          },
        },
        state.ui,
      );
    }

    case "SET_DYNAMIC_SEGMENT_ID_MAP":
      return deriveDerivedState({ ...state, dynamicSegmentIdMap: action.payload }, state.ui);

    case "ARCHIVE_STATUS_CHANGED": {
      const details = state.momentDetails.data;
      const followUp = details?.followUp;

      if (!details || !followUp) return state;

      return deriveDerivedState(
        {
          ...state,
          saveState: { saving: false, error: null },
          momentDetails: {
            ...state.momentDetails,
            data: {
              ...details,
              followUp: {
                ...followUp,
                status: action.payload ? "archived" : followUp.running ? "active" : "paused",
              },
            },
          },
        },
        state.ui,
      );
    }
  }
}

function deriveDerivedState(
  state: FollowUpPageState,
  newUi: FollowUpPageState["ui"],
): FollowUpPageState {
  const channelInfo = {
    channel: newUi.channel,
    channelData: newUi.channelData,
  } as FollowUpChannelWithData;

  return {
    ...state,
    ui: newUi,
    errors: {
      display: state.errors.display,
      errors: validateFollowUp(newUi, state.useLexicalEditor),
    },
    derivedData: {
      toTarget: getToTarget({
        target_connection_type:
          state.momentDetails.data?.momentFollowUpDetails.target_connection_type ?? null,
        target_manager: state.momentDetails.data?.momentFollowUpDetails.target_manager ?? false,
      }),
      saveData: followUpToHandleSaveMoment(
        newUi,
        state.momentDetails.data,
        state.derivedData.saveData,
        state.useLexicalEditor,
        state.emailMoment,
        state.dynamicSegmentIdMap,
      ),
      usesRestrictedData: usesRestrictedSegmentOrEmail({
        restrictedEmails: state.momentDetails.data?.restrictions.email_from || [],
        restrictedSegments: state.momentDetails.data?.restrictions.segment || [],
        email_from_address: isEmailChannelWithData(channelInfo)
          ? channelInfo.channelData.email_sender
          : null,
        segment_id: null,
        customEmailAddresses: state.momentDetails.data?.customEmailAddresses || [],
      }),
      uiDisabled: uiState(state.momentDetails.data?.followUp),
      isDirty:
        !isEqual(newUi, state.initialUi) || !isEqual(state.emailMoment, state.initialEmailMoment),
    },
  };
}

function uiState(
  followUpData?: FollowUpDetailsWithConfig,
): FollowUpPageState["derivedData"]["uiDisabled"] {
  if (!followUpData) {
    return {
      form: false,
      buttons: {
        publish: false,
        save: false,
        sendTest: false,
        pause: true,
      },
    };
  }

  const archived = followUpData.status === "archived" || followUpData.status === "finished";

  return {
    form: archived || followUpData.running,
    buttons: {
      publish: archived || followUpData.running,
      save: archived || followUpData.running,
      sendTest: archived || followUpData.running,
      pause:
        archived ||
        !followUpData.running ||
        !!(followUpData.immediate && (followUpData.running || followUpData.sent)),
    },
  };
}

function validateFollowUp(
  data: FollowUpSchemaType,
  useLexicalEditor: boolean,
): $ZodErrorTree<FollowUpSchemaType> | null {
  const schema = useLexicalEditor ? followUpSchema : followUpSchemaNewsletter;
  const { success, error } = schema.safeParse(data);
  if (success) return null;
  return z4.treeifyError(error);
}

export function makeDefaultFollowUpState(
  followUpMomentDetailsConfig: FollowUpMomentDetailsConfig,
): FollowUpPageState {
  const defaultUi: FollowUpPageState["ui"] = {
    mode: "scheduled" as const,
    title: "",
    reason: null,
    channel: "work_email" as const,
    channelData: {
      email_sender: config.defaultEmailFrom,
      email_subject: "",
      email_design_id: null,
    },
    design: null,
    schedule: {
      type: "scheduled",
      amount: null,
      period: null,
      time: null,
      timezone: null,
      allow_weekends: false,
    },
  };

  return {
    followUpMomentDetailsConfig,
    momentDetails: { data: null, loading: true, error: null },
    ui: defaultUi,
    initialUi: defaultUi,
    useLexicalEditor: true,
    initialUseLexicalEditor: true,
    emailMoment: null,
    initialEmailMoment: null,
    dynamicSegmentIdMap: null,
    saveState: { saving: false, error: null },
    errors: {
      display: false,
      errors: validateFollowUp(defaultUi, true),
    },
    derivedData: {
      toTarget: { toTarget: "employee", target_connection_type: null, isManagerConnection: false },
      saveData: null,
      usesRestrictedData: false,
      uiDisabled: {
        form: false,
        buttons: {
          publish: false,
          save: false,
          sendTest: false,
          pause: false,
        },
      },
      isDirty: false,
    },
  };
}
